<script setup>
import { onMounted, onUnmounted, ref, watch, computed, provide, defineAsyncComponent } from 'vue'
import { useTrackingStore } from './stores/trackingStore'
import { RouterView, useRoute, useRouter } from 'vue-router'
const CookieBanner = defineAsyncComponent(() => import('./components/CookieBanner.vue'))
const GlobalSearch = defineAsyncComponent(() => import('./components/GlobalSearch.vue'))
import { useAnalytics } from './composables/useAnalytics'
import { useAuthStore } from './stores/authStore'
import { useCartStore } from './stores/cartStore'
import { useSettingsStore } from './stores/settingsStore'
import { usePromotionsStore } from './stores/promotionsStore'
import { useTrackingTags } from './composables/useTrackingTags'
import { useDataLayer } from './composables/useDataLayer'
const LoginModal = defineAsyncComponent(() => import('./components/LoginModal.vue'))
import BackToTop from './components/BackToTop.vue'
const AIProductRecommendation = defineAsyncComponent(() => import('./components/AIProductRecommendation.vue'))
import ToastContainer from './components/ToastContainer.vue'
const ConfirmModal = defineAsyncComponent(() => import('./components/ConfirmModal.vue'))
const CompareFloatingBar = defineAsyncComponent(() => import('./components/CompareFloatingBar.vue'))
const HistoryFloatingButton = defineAsyncComponent(() => import('./components/HistoryFloatingButton.vue'))
const Maintenance = defineAsyncComponent(() => import('./pages/Maintenance.vue'))
import MainFooter from './components/MainFooter.vue'
import PaydayTopBanner from './components/PaydayTopBanner.vue'
import { useWishlistStore } from './stores/wishlistStore'
import { getOptimizedImageUrl } from './utils/image'

// Auth state
const authStore = useAuthStore()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()
const settingsStore = useSettingsStore()
const promotionsStore = usePromotionsStore()
const isLoginModalOpen = ref(false)
provide('openLoginModal', () => { isLoginModalOpen.value = true })

const desktopAvatarError = ref(false)
const mobileAvatarError = ref(false)

watch(() => authStore.currentUser?.avatar_url, () => {
  desktopAvatarError.value = false
  mobileAvatarError.value = false
})

// Floating contact hub state
const isContactHubOpen = ref(false)
const contactHubRef = ref(null)

const toggleContactHub = () => {
  isContactHubOpen.value = !isContactHubOpen.value
}

const handleClickOutsideContactHub = (e) => {
  if (isContactHubOpen.value && contactHubRef.value && !contactHubRef.value.contains(e.target)) {
    isContactHubOpen.value = false
  }
}

// Search state
const isSearchOpen = ref(false)
const headerSearchQuery = ref('')
const handleHeaderSearch = () => {
  if (headerSearchQuery.value.trim()) {
    router.push({ path: '/products', query: { search: headerSearchQuery.value.trim() } })
    headerSearchQuery.value = ''
  }
}

// Nav categories dropdown
const navCategories = ref([])
const isProductsDropdownOpen = ref(false)
const isMobileProductsOpen = ref(false)
let dropdownTimer = null

const openProductsDropdown = () => {
  clearTimeout(dropdownTimer)
  isProductsDropdownOpen.value = true
}
const closeProductsDropdown = () => {
  dropdownTimer = setTimeout(() => {
    isProductsDropdownOpen.value = false
  }, 200)
}

const spotlightCategory = computed(() => {
  return navCategories.value.find(c => c.image_url) || null
})
const spotlightImageUrl = computed(() => {
  return spotlightCategory.value?.image_url || '/images/categories/category-1.webp'
})

// About dropdown
const isAboutDropdownOpen = ref(false)
const isMobileAboutOpen = ref(false)
let aboutDropdownTimer = null

const openAboutDropdown = () => {
  clearTimeout(aboutDropdownTimer)
  isAboutDropdownOpen.value = true
}
const closeAboutDropdown = () => {
  aboutDropdownTimer = setTimeout(() => {
    isAboutDropdownOpen.value = false
  }, 200)
}
const contactPhones = computed(() => settingsStore.contactPhones)
const contactEmails = computed(() => settingsStore.contactEmails)
const primaryContactPhone = computed(() => {
  if (settingsStore.storePhone) return settingsStore.storePhone
  const phones = settingsStore.contactPhones
  if (Array.isArray(phones) && phones.length > 0) {
    const first = phones[0]
    const val = typeof first === 'object' && first !== null ? (first.value || first.phone || '') : String(first)
    if (val) return val
  }
  return settingsStore.contactPhone || ''
})
const primaryContactEmail = computed(() => {
  if (settingsStore.storeEmail) return settingsStore.storeEmail
  const emails = settingsStore.contactEmails
  if (Array.isArray(emails) && emails.length > 0) {
    const first = emails[0]
    const val = typeof first === 'object' && first !== null ? (first.value || first.email || '') : String(first)
    if (val) return val
  }
  return settingsStore.contactEmail || ''
})
const contactLines = computed(() => settingsStore.contactLines)
const contactFacebookUrl = computed(() => settingsStore.contactFacebookUrl)
const contactTiktokUrl = computed(() => settingsStore.contactTiktokUrl)
const contactYoutubeUrl = computed(() => settingsStore.contactYoutubeUrl)
const contactMapUrl = computed(() => settingsStore.resolvedContactMapUrl)

// Newsletter state and methods moved to components/NewsletterSection.vue

// Initialize tracking
const trackingStore = useTrackingStore()
const route = useRoute()
const router = useRouter()

// Watch authentication status for redirection
watch(() => authStore.isAuthenticated, (isAuth) => {
  if (isAuth) {
    if (route.path !== '/cart' && route.path !== '/checkout') {
      router.push('/profile')
    }
  } else {
    if (route.path === '/profile' || route.path.startsWith('/profile/') || route.path === '/checkout') {
      router.push('/')
    }
  }
})

// Watch authModalTrigger from authStore.openAuthModal()
watch(() => authStore.authModalTrigger, (val) => {
  if (val > 0) {
    isLoginModalOpen.value = true
  }
})

const handleWishlistNavClick = () => {
  isMobileMenuOpen.value = false
  if (authStore.isAuthenticated) {
    router.push({ path: '/profile', query: { tab: 'wishlist' } })
  } else {
    isLoginModalOpen.value = true
  }
}

// Watch route query to open login modal automatically (e.g. from redirect links)
watch(() => route.query.login, (val) => {
  if (val === 'true') {
    isLoginModalOpen.value = true
    // Clean up the query parameter to avoid opening it again on reload/navigation
    const query = { ...route.query }
    delete query.login
    router.replace({ path: route.path, query })
  }
}, { immediate: true })


// Initialize detailed analytics (auto-tracks pageviews, scroll depth, time on page)
const { trackPageView } = useAnalytics()
const { initTrackingConsent, cleanupTrackingConsent } = useTrackingTags()
const { trackPageView: trackDataLayerPageView } = useDataLayer()

// Re-track on route change
watch(() => route.fullPath, () => {
  if (!route.path.startsWith('/admin')) {
    trackPageView()
    trackDataLayerPageView(route.path, document.title)
  }
})

// Dark Mode Logic
const isDark = ref(false)
const isMobileMenuOpen = ref(false)
const isScrolled = ref(false)

const handleScroll = () => {
  if (window.scrollY > 50) {
    isScrolled.value = true
  } else {
    isScrolled.value = false
  }
}

const isDarkHeader = computed(() => {
  const darkPages = ['/', '/contact', '/promotions', '/services']
  return darkPages.includes(route.path) && !isScrolled.value
})

const isAdminRoute = computed(() => {
  return route.path.startsWith('/admin') || window.location.pathname.startsWith('/admin')
})

// Check if current route has mobile sticky bottom conversion bar
const hasMobileBottomBar = computed(() => {
  return !isAdminRoute.value && route.path.startsWith('/products/') && route.path !== '/products' && !route.path.startsWith('/products/category/')
})

// Active route detection for nav highlighting
const isActiveRoute = (path) => {
  if (path === '/') return route.path === '/'
  return route.path === path || route.path.startsWith(path + '/')
}

const toggleDarkMode = () => {
  isDark.value = !isDark.value
  if (isDark.value) {
    document.documentElement.classList.add('dark')
    localStorage.setItem('theme', 'dark')
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('theme', 'light')
  }
}

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value
}

// Close mobile menu and dropdowns on route change
watch(route, () => {
  isMobileMenuOpen.value = false
  isProductsDropdownOpen.value = false
  isAboutDropdownOpen.value = false
  if (typeof document !== 'undefined') {
    document.body.classList.remove('overflow-hidden')
  }
})

// Body scroll locking and Escape key handling for mobile drawer
watch(isMobileMenuOpen, (isOpen) => {
  if (typeof document !== 'undefined') {
    if (isOpen) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }
})

const handleGlobalKeyDown = (e) => {
  if (e.key === 'Escape') {
    if (isSearchOpen.value) isSearchOpen.value = false
    if (isMobileMenuOpen.value) isMobileMenuOpen.value = false
    if (isProductsDropdownOpen.value) isProductsDropdownOpen.value = false
    if (isAboutDropdownOpen.value) isAboutDropdownOpen.value = false
  }
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault()
    isSearchOpen.value = !isSearchOpen.value
  }
}

// Sync Google Search Console verification meta tag dynamically from settings
watch(() => settingsStore.settings?.google_site_verification, (newVal) => {
  if (newVal) {
    let meta = document.getElementById('gsc-meta-tag') || document.querySelector('meta[name="google-site-verification"]')
    if (meta) {
      meta.setAttribute('content', newVal)
    } else {
      meta = document.createElement('meta')
      meta.name = 'google-site-verification'
      meta.id = 'gsc-meta-tag'
      meta.content = newVal
      document.head.appendChild(meta)
    }
  }
}, { immediate: true })

onMounted(() => {
  // Always fetch latest settings from admin database
  settingsStore.fetchSettings()

  // Global image error fallback: when cached thumbnails 404, fall back to original images directly
  document.addEventListener('error', (event) => {
    const img = event.target
    if (!img || img.tagName !== 'IMG') return

    const src = img.getAttribute('src') || ''
    // Only handle /uploads/cache/ URLs
    if (!src.includes('/uploads/') || !src.includes('/cache/')) return
    // Prevent infinite loops
    if (img.dataset.fallbackAttempted === 'original') return

    // Parse cache path: /uploads[/subfolder]/cache/basename-WIDTH.ext
    const cacheMatch = src.match(/^\/uploads\/(?:(.+)\/)?cache\/([^/]+)-(\d+)\.([a-zA-Z0-9]+)$/)
    if (!cacheMatch) return

    // Fallback directly to the original full-size image
    const subfolder = cacheMatch[1] || ''
    const baseName = cacheMatch[2]
    const ext = cacheMatch[4]
    const originalPath = subfolder
      ? `/uploads/${subfolder}/${baseName}.${ext}`
      : `/uploads/${baseName}.${ext}`
    img.dataset.fallbackAttempted = 'original'
    img.src = originalPath
  }, true) // 'true' = capture phase, intercepts before component-level handlers


  // Capture Registration Source
  if (!localStorage.getItem('registration_source')) {
    try {
      const urlParams = new URLSearchParams(window.location.search)
      let source = 'organic'
      if (urlParams.get('utm_source') || urlParams.get('utm_medium')) {
        source = `utm_source=${urlParams.get('utm_source') || 'unknown'}&utm_medium=${urlParams.get('utm_medium') || 'none'}`
      } else if (urlParams.get('fbclid')) {
        source = 'facebook_ad'
      } else if (urlParams.get('ref')) {
        source = `ref=${urlParams.get('ref')}`
      } else if (document.referrer && !document.referrer.includes(window.location.hostname)) {
        source = new URL(document.referrer).hostname
      }
      localStorage.setItem('registration_source', source)
    } catch (e) {
      console.error('Failed to parse registration source', e)
    }
  }

  // Initialize Session
  trackingStore.initSession()
  trackingStore.trackEvent({ type: 'page_view', path: window.location.pathname })

  // Initialize Cart & Active Promotions
  if (!window.location.pathname.startsWith('/admin')) {
    cartStore.fetchCart()
    promotionsStore.fetchActivePromotions()
  }

  // Load categories for nav dropdown
  fetch('/api/categories')
    .then(r => r.json())
    .then(data => { if (data.success) navCategories.value = data.data })
    .catch(e => console.error('Failed to load nav categories', e))
  
  // Initialize Theme (Check LocalStorage or System Prefs)
  if (localStorage.getItem('theme') === 'dark') {
    isDark.value = true
    document.documentElement.classList.add('dark')
  } else {
    isDark.value = false
    document.documentElement.classList.remove('dark')
  }
  // Handle Scroll
  window.addEventListener('scroll', handleScroll, { passive: true })

  // Initialize tracking consent & push initial dataLayer event
  initTrackingConsent()
  if (!route.path.startsWith('/admin')) {
    trackDataLayerPageView(route.path, document.title)
  }

  // Click outside to close contact hub
  document.addEventListener('click', handleClickOutsideContactHub)

  // Global keydown handler
  window.addEventListener('keydown', handleGlobalKeyDown)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  window.removeEventListener('keydown', handleGlobalKeyDown)
  cleanupTrackingConsent()
  document.removeEventListener('click', handleClickOutsideContactHub)
  if (typeof document !== 'undefined') {
    document.body.classList.remove('overflow-hidden')
  }
})
</script>

<template>
  <div class="min-h-screen flex flex-col font-sans transition-colors duration-500 bg-[#f8f9fa] dark:bg-[#0a0f16] text-gray-900 dark:text-gray-100 selection:bg-orange-200 dark:selection:bg-orange-800/50">
    
    <!-- Maintenance Mode -->
    <Maintenance v-if="settingsStore.maintenanceModeEnabled && !isAdminRoute" />

    <template v-else>
      <!-- Premium Woodland Navigation matching reference design with complete Menu Bar -->
      <header v-if="!isAdminRoute"
        :class="[
          'z-50 sticky top-0 left-0 w-full transition-all duration-300 shadow-xl border-b border-white/10 overflow-x-clip',
          isScrolled ? 'backdrop-blur-md' : ''
        ]"
      >
        <!-- Neutral Black Topbar Background -->
        <div class="relative bg-[#090a0c] overflow-visible">
          <!-- Clean Dark Gradient Overlay for pristine text readability -->
          <div class="absolute inset-0 bg-gradient-to-r from-[#090a0c] via-[#0e0f13] to-[#090a0c] backdrop-blur-md"></div>
          
          <div class="relative z-10">
            <!-- Payday Live Countdown Announcement Bar -->
            <PaydayTopBanner />

            <!-- Top Utility Bar (hides on scroll to keep sticky nav sleek) -->
            <div v-show="!isScrolled" class="hidden sm:block border-b border-white/10 text-xs text-gray-300 py-1.5 transition-all duration-300">
              <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between gap-4">
                <div class="hidden sm:flex items-center gap-3.5 text-orange-200/90 font-medium min-w-0">
                  <span class="inline-flex items-center gap-1.5 truncate">
                    <span class="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse shrink-0"></span>
                    <span class="truncate">{{ settingsStore.storeDescription || 'พื้นที่กลางแจ้ง ให้ชีวิตคุณกว้างขึ้น | Outdoor Lander Official' }}</span>
                  </span>
                  <template v-if="primaryContactPhone">
                    <span class="hidden md:inline text-white/30 shrink-0">|</span>
                    <a :href="`tel:${primaryContactPhone}`" class="hidden md:inline-flex items-center gap-1.5 hover:text-white transition-colors shrink-0" title="เบอร์โทรติดต่อ">
                      <svg class="w-3.5 h-3.5 text-orange-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                      </svg>
                      <span>{{ primaryContactPhone }}</span>
                    </a>
                  </template>
                  <template v-if="primaryContactEmail">
                    <span class="hidden md:inline text-white/30 shrink-0">|</span>
                    <a :href="`mailto:${primaryContactEmail}`" class="hidden md:inline-flex items-center gap-1.5 hover:text-white transition-colors shrink-0" title="อีเมลติดต่อ">
                      <svg class="w-3.5 h-3.5 text-orange-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                      </svg>
                      <span>{{ primaryContactEmail }}</span>
                    </a>
                  </template>
                </div>
                <div class="flex items-center gap-5 ml-auto text-[11px] font-semibold tracking-wider">
                  <router-link to="/track-order" class="hover:text-orange-300 transition-colors flex items-center gap-1">
                    <svg class="w-3.5 h-3.5 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"/></svg>
                    ติดตามพัสดุ
                  </router-link>
                  <span class="text-white/20">|</span>
                  <router-link to="/contact" class="hover:text-orange-300 transition-colors flex items-center gap-1">
                    <svg class="w-3.5 h-3.5 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
                    ศูนย์ช่วยเหลือ
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Main Topbar Row (Logo, Search, Actions) -->
            <div 
              :class="[
                'max-w-screen-2xl mx-auto px-3.5 sm:px-6 lg:px-10 flex items-center justify-between gap-2 sm:gap-3 lg:gap-6 transition-all duration-300',
                isScrolled ? 'py-2 lg:py-2.5' : 'py-3 lg:py-3.5'
              ]"
            >
              <!-- Brand Logo + Tagline -->
              <router-link to="/" class="group flex items-center gap-2 sm:gap-3 min-w-0">
                <template v-if="settingsStore.storeLogo">
                   <img
                     :src="getOptimizedImageUrl(settingsStore.storeLogo, 300)"
                     :srcset="`${getOptimizedImageUrl(settingsStore.storeLogo, 200)} 200w, ${getOptimizedImageUrl(settingsStore.storeLogo, 300)} 300w, ${getOptimizedImageUrl(settingsStore.storeLogo, 400)} 400w`"
                     sizes="(max-width: 640px) 160px, (max-width: 1024px) 210px, 260px"
                     alt="Store Logo"
                     width="200"
                     height="50"
                     class="h-8 sm:h-9 lg:h-11 max-w-[160px] xs:max-w-[210px] sm:max-w-none w-auto object-contain drop-shadow-md transition-transform duration-300 group-hover:scale-105"
                     fetchpriority="high"
                   />
                </template>
                <template v-else>
                  <div class="w-9 h-9 sm:w-10 sm:h-10 lg:w-11 lg:h-11 bg-gradient-to-br from-amber-500 to-orange-600 text-white rounded-xl flex items-center justify-center shadow-lg shadow-black/50 border border-orange-400/40 group-hover:scale-105 transition-all duration-300 shrink-0">
                    <svg class="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 2L4 12h3l-2 5h4v5h2v-5h4l-2-5h3L12 2z" />
                    </svg>
                  </div>
                  <div class="flex flex-col min-w-0">
                    <span class="text-sm xs:text-base sm:text-lg lg:text-xl font-black tracking-wider sm:tracking-widest text-white uppercase drop-shadow font-sans truncate">
                      {{ settingsStore.storeName || 'OUTDOOR LANDER' }}
                    </span>
                    <span class="text-[10px] text-orange-300/80 font-medium tracking-wider hidden sm:block -mt-0.5">
                      พื้นที่กลางแจ้ง ให้ชีวิตคุณกว้างขึ้น
                    </span>
                  </div>
                </template>
              </router-link>

              <!-- Category Button: [ ⊞ หมวดหมู่ทั้งหมด ˅ ] -->
              <div class="relative hidden md:block shrink-0" @mouseenter="openProductsDropdown" @mouseleave="closeProductsDropdown">
                <button 
                  type="button"
                  @click="isProductsDropdownOpen = !isProductsDropdownOpen"
                  class="group relative overflow-hidden bg-gradient-to-r from-amber-500 via-orange-600 to-orange-700 hover:from-amber-400 hover:to-orange-500 text-white px-4 py-2.5 rounded-2xl font-bold text-xs lg:text-sm shadow-lg shadow-black/50 border border-orange-400/35 flex items-center gap-2.5 transition-all duration-300 hover:shadow-orange-500/30 hover:-translate-y-0.5 cursor-pointer"
                >
                  <!-- Subtle shimmer overlay -->
                  <span class="absolute inset-0 w-1/2 h-full bg-white/10 skew-x-12 -translate-x-full group-hover:translate-x-[300%] transition-transform duration-1000"></span>
                  
                  <div class="w-5 h-5 rounded-lg bg-white/15 flex items-center justify-center p-0.5 shrink-0 group-hover:scale-110 transition-transform">
                    <svg class="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M4 4h4v4H4V4zm6 0h4v4h-4V4zm6 0h4v4h-4V4zM4 10h4v4H4v-4zm6 0h4v4h-4v-4zm6 0h4v4h-4v-4zM4 16h4v4H4v-4zm6 0h4v4h-4v-4zm6 0h4v4h-4v-4z"/>
                    </svg>
                  </div>
                  <span class="tracking-wide">หมวดหมู่ทั้งหมด</span>
                  <svg class="w-3.5 h-3.5 text-orange-200 transition-transform duration-300" :class="isProductsDropdownOpen ? 'rotate-180 text-white' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/>
                  </svg>
                </button>

                <!-- Category Mega Menu Dropdown -->
                <transition name="dropdown">
                  <div v-if="isProductsDropdownOpen" class="absolute top-full -left-2 pt-3 z-50">
                    <div class="bg-white/95 dark:bg-[#0c141f]/95 backdrop-blur-2xl rounded-3xl shadow-[0_30px_90px_-20px_rgba(0,0,0,0.45)] border border-orange-500/20 dark:border-orange-500/30 p-4 sm:p-5 w-[880px] max-w-[94vw] overflow-hidden text-left">
                      
                      <!-- Dropdown Header Bar -->
                      <div class="flex items-center justify-between pb-3.5 mb-3.5 border-b border-gray-100 dark:border-white/10">
                        <div class="flex items-center gap-3">
                          <div class="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shadow-md shadow-orange-950/20">
                            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
                          </div>
                          <div>
                            <div class="flex items-center gap-2">
                              <h4 class="text-sm font-black text-gray-900 dark:text-white tracking-wide">หมวดหมู่สินค้ากลางแจ้ง</h4>
                              <span class="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300">พรีเมียม</span>
                            </div>
                            <p class="text-[11px] text-gray-500 dark:text-gray-400">คัดสรรโครงสร้างคุณภาพ ทนแดด ทนฝน ไม่เป็นสนิม 100%</p>
                          </div>
                        </div>

                        <router-link 
                          to="/products" 
                          @click="isProductsDropdownOpen = false" 
                          class="group inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-orange-50 hover:bg-orange-100 dark:bg-white/5 dark:hover:bg-orange-950/40 text-orange-800 dark:text-orange-300 text-xs font-bold transition-all duration-200 border border-orange-200/50 dark:border-orange-500/20"
                        >
                          <span>ดูสินค้าทั้งหมด</span>
                          <svg class="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/>
                          </svg>
                        </router-link>
                      </div>

                      <!-- Main Body: Categories Grid + Spotlight Card -->
                      <div class="flex gap-4">
                        
                        <!-- Left: Categories Grid (2 Columns) -->
                        <div class="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[380px] overflow-y-auto pr-1">
                          <router-link 
                            v-for="cat in navCategories" 
                            :key="cat.id || cat.name" 
                            :to="`/products/category/${encodeURIComponent(cat.slug || cat.name)}`" 
                            @click="isProductsDropdownOpen = false"
                            class="group relative flex items-center gap-3 p-2.5 rounded-2xl bg-gray-50/70 hover:bg-orange-50/80 dark:bg-white/[0.03] dark:hover:bg-orange-950/30 border border-gray-100 dark:border-white/5 hover:border-orange-400/40 hover:shadow-md hover:shadow-orange-950/5 transition-all duration-200"
                          >
                            <!-- Category Icon Container with Deep Emerald Gradient (makes white silhouette crisp) -->
                            <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1c120c] via-[#2a170d] to-[#140b07] text-white flex items-center justify-center p-2.5 shrink-0 shadow-md shadow-black/40 border border-orange-500/30 group-hover:scale-105 group-hover:from-orange-900 group-hover:to-neutral-900 transition-all duration-200">
                              <img 
                                v-if="cat.icon_url" 
                                :src="cat.icon_url" 
                                class="w-full h-full object-contain filter drop-shadow brightness-110" 
                                :alt="cat.name"
                              />
                              <img 
                                v-else-if="cat.image_url" 
                                :src="cat.image_url" 
                                class="w-full h-full object-cover rounded-lg" 
                                :alt="cat.name"
                              />
                              <svg v-else class="w-6 h-6 text-orange-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 002-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                              </svg>
                            </div>

                            <!-- Category Info -->
                            <div class="flex-1 min-w-0">
                              <div class="text-xs font-bold text-gray-900 dark:text-white group-hover:text-orange-700 dark:group-hover:text-orange-400 transition-colors truncate">
                                {{ cat.name }}
                              </div>
                              <div class="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5 group-hover:text-gray-700 dark:group-hover:text-gray-300">
                                {{ cat.description || 'ดูสินค้าทั้งหมดในหมวดหมู่นี้' }}
                              </div>
                            </div>

                            <!-- Hover Arrow Pill -->
                            <div class="w-5 h-5 rounded-full bg-orange-100/70 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 flex items-center justify-center opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all duration-200 shrink-0">
                              <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/>
                              </svg>
                            </div>
                          </router-link>
                        </div>

                        <!-- Right: Spotlight Showcase Card (High-end Brand Accent) -->
                        <div class="w-[260px] shrink-0 hidden lg:flex flex-col justify-between bg-gradient-to-b from-orange-950/15 via-orange-900/10 to-neutral-950/30 dark:from-orange-950/40 dark:to-slate-900/80 rounded-2xl p-3.5 border border-orange-500/20 shadow-inner">
                          <div>
                            <!-- Highlight Badge -->
                            <div class="flex items-center justify-between mb-2.5">
                              <span class="inline-flex items-center gap-1.5 text-[10px] font-black tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500 border border-amber-500/20">
                                <svg class="w-3 h-3 text-amber-500 shrink-0" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                                <span>ไฮไลท์คัดสรร</span>
                              </span>
                              <span class="text-[10px] text-orange-600 dark:text-orange-400 font-semibold">แนะนำ</span>
                            </div>

                            <!-- Showcase Photo -->
                            <div class="relative rounded-xl overflow-hidden aspect-[16/10] mb-2.5 shadow-md border border-white/20 dark:border-white/10 group">
                              <img 
                                :src="spotlightImageUrl" 
                                alt="Outdoor Shed" 
                                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                              <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end p-2">
                                <span class="text-[10px] text-white font-bold tracking-tight line-clamp-1">
                                  {{ spotlightCategory?.name || 'บ้านเก็บของทนแดด UV 100%' }}
                                </span>
                              </div>
                            </div>

                            <p class="text-[11px] text-gray-600 dark:text-gray-300 leading-relaxed font-medium line-clamp-2">
                              {{ spotlightCategory?.description || 'วัสดุเกรดพรีเมียม ไม่ผุพัง ไม่เป็นสนิม แข็งแรงทนทานมาตรฐานสากล' }}
                            </p>
                          </div>

                          <!-- Quick Tools -->
                          <div class="pt-3 mt-2 border-t border-orange-500/15 space-y-1.5">
                            <router-link 
                              to="/space-calculator" 
                              @click="isProductsDropdownOpen = false"
                              class="flex items-center justify-between p-2 rounded-xl bg-white/70 hover:bg-white dark:bg-white/5 dark:hover:bg-white/10 text-gray-900 dark:text-white text-xs font-bold transition-all shadow-xs"
                            >
                              <span class="flex items-center gap-1.5">
                                <svg class="w-3.5 h-3.5 text-orange-600 dark:text-orange-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 20h16M4 20l12-12m-6 0l4 4M4 20l4-4"/></svg>
                                <span>คำนวณพื้นที่</span>
                              </span>
                              <span class="text-[10px] text-orange-600 dark:text-orange-400">ฟรี →</span>
                            </router-link>

                            <router-link 
                              to="/quotation" 
                              @click="isProductsDropdownOpen = false"
                              class="flex items-center justify-between p-2 rounded-xl bg-white/70 hover:bg-white dark:bg-white/5 dark:hover:bg-white/10 text-gray-900 dark:text-white text-xs font-bold transition-all shadow-xs"
                            >
                              <span class="flex items-center gap-1.5">
                                <svg class="w-3.5 h-3.5 text-orange-600 dark:text-orange-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                <span>ขอใบเสนอราคา</span>
                              </span>
                              <span class="text-[10px] text-orange-600 dark:text-orange-400">ด่วน →</span>
                            </router-link>
                          </div>
                        </div>

                      </div>

                      <!-- Bottom Quick Assistance Strip -->
                      <div class="flex items-center justify-between pt-3 mt-3 border-t border-gray-100 dark:border-white/10 text-[11px]">
                        <div class="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                          <span class="w-2 h-2 rounded-full bg-orange-400 animate-pulse"></span>
                          <span>มีบริการวัดพื้นที่หน้างานและติดตั้งโดยทีมช่างมืออาชีพ</span>
                        </div>
                        <div class="flex items-center gap-3">
                          <a 
                            v-if="primaryContactPhone" 
                            :href="`tel:${primaryContactPhone}`" 
                            class="font-bold text-orange-700 dark:text-orange-400 hover:underline flex items-center gap-1"
                          >
                            <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                            </svg>
                            <span>ปรึกษาทีมงาน: {{ primaryContactPhone }}</span>
                          </a>
                          <span v-if="primaryContactPhone && primaryContactEmail" class="text-gray-300 dark:text-gray-600">|</span>
                          <a 
                            v-if="primaryContactEmail" 
                            :href="`mailto:${primaryContactEmail}`" 
                            class="font-bold text-orange-700 dark:text-orange-400 hover:underline flex items-center gap-1"
                            title="อีเมลติดต่อ"
                          >
                            <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                            </svg>
                            <span>{{ primaryContactEmail }}</span>
                          </a>
                        </div>
                      </div>

                    </div>
                  </div>
                </transition>
              </div>

              <!-- Center: Wide Pill Search Bar (Desktop only) -->
              <div class="hidden lg:flex flex-1 max-w-xl mx-auto">
                <div 
                  @click="isSearchOpen = true" 
                  class="relative flex items-center w-full cursor-pointer group"
                  role="button"
                  tabindex="0"
                  @keydown.enter="isSearchOpen = true"
                  @keydown.space.prevent="isSearchOpen = true"
                  title="ค้นหาสินค้า (⌘K)"
                >
                  <div class="relative w-full flex items-center">
                    <input 
                      type="text" 
                      readonly
                      placeholder="ค้นหาบ้านเก็บของ โรงเรือน กล่องเก็บของ อุปกรณ์กลางแจ้ง..." 
                      class="w-full bg-white dark:bg-[#111822] text-gray-900 dark:text-white placeholder-gray-500 text-xs sm:text-sm pl-4 pr-24 py-2 sm:py-2.5 rounded-full border border-white/20 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400 shadow-inner cursor-pointer"
                    />
                    <div class="absolute right-1.5 flex items-center gap-1.5 pointer-events-none">
                      <span class="hidden xl:inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded border border-gray-300 dark:border-gray-600">⌘K</span>
                      <div class="bg-gradient-to-r from-[#ff7a00] to-[#ea580c] hover:from-[#ff8800] hover:to-[#f97316] text-white px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors flex items-center gap-1 shadow-sm">
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
                        <span class="hidden sm:inline">ค้นหา</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right: Quick Action Items -->
              <div class="flex items-center gap-1.5 sm:gap-3 xl:gap-4 shrink-0">
                <!-- Wishlist (Tablet/Desktop) -->
                <button 
                  v-if="settingsStore.isWishlistEnabled"
                  type="button"
                  @click="handleWishlistNavClick" 
                  class="hidden sm:flex relative flex-col items-center justify-center text-white/90 hover:text-rose-400 transition-colors group px-2 py-1 cursor-pointer"
                  title="รายการโปรด"
                >
                  <div class="relative">
                    <svg class="w-5 h-5 text-gray-200 group-hover:text-rose-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <span 
                      v-if="wishlistStore.items && wishlistStore.items.length > 0" 
                      class="absolute -top-1.5 -right-2 bg-rose-500 text-white text-[9px] font-black px-1.5 py-0.2 rounded-full min-w-[16px] text-center shadow"
                    >
                      {{ wishlistStore.items.length }}
                    </span>
                  </div>
                  <span class="text-[10px] font-semibold mt-0.5 tracking-tight hidden sm:block">รายการโปรด</span>
                </button>

                <!-- Search Button (ข้างปุ่มตะกร้าสินค้า - สำหรับโหมดมือถือและแท็บเล็ต) -->
                <button 
                  type="button"
                  @click="isSearchOpen = true" 
                  class="relative flex flex-col items-center justify-center text-white/90 hover:text-orange-300 transition-colors group px-2 py-1 cursor-pointer lg:hidden"
                  title="ค้นหาสินค้า"
                  aria-label="ค้นหาสินค้า"
                >
                  <div class="relative">
                    <svg class="w-5 h-5 text-gray-200 group-hover:text-orange-300 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <span class="text-[10px] font-semibold mt-0.5 tracking-tight hidden sm:block">ค้นหา</span>
                </button>

                <!-- Cart (Always visible with badge) -->
                <router-link 
                  v-if="settingsStore.isOnlineShoppingEnabled"
                  to="/cart" 
                  class="relative flex flex-col items-center justify-center text-white/90 hover:text-orange-300 transition-colors group px-2 py-1"
                  title="ตะกร้าสินค้า"
                >
                  <div class="relative">
                    <svg class="w-5 h-5 text-gray-200 group-hover:text-orange-300 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    <span 
                      v-if="cartStore.cartTotal > 0" 
                      class="absolute -top-1.5 -right-2 bg-amber-500 text-gray-950 text-[9px] font-black px-1.5 py-0.2 rounded-full min-w-[16px] text-center shadow"
                    >
                      {{ cartStore.cartTotal > 99 ? '99+' : cartStore.cartTotal }}
                    </span>
                  </div>
                  <span class="text-[10px] font-semibold mt-0.5 tracking-tight hidden sm:block">ตะกร้าสินค้า</span>
                </router-link>

                <!-- Account / Profile Dropdown (Tablet/Desktop) -->
                <div v-if="settingsStore.isOnlineShoppingEnabled && authStore.isAuthenticated" class="hidden md:block relative group">
                  <button aria-label="เมนูบัญชีผู้ใช้" class="flex flex-col items-center justify-center text-white/90 hover:text-orange-300 transition-colors px-1 py-1 cursor-pointer">
                    <img v-if="authStore.currentUser?.avatar_url && !desktopAvatarError" :src="authStore.currentUser?.avatar_url" @error="desktopAvatarError = true" class="w-6 h-6 rounded-full object-cover border border-orange-400">
                    <div v-else class="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center text-white text-[10px] font-black">
                      {{ authStore.currentUser?.first_name?.charAt(0)?.toUpperCase() || authStore.currentUser?.username?.charAt(0)?.toUpperCase() || 'U' }}
                    </div>
                    <span class="text-[10px] font-semibold mt-0.5 tracking-tight truncate max-w-[60px] hidden sm:block">
                      {{ authStore.currentUser?.first_name || 'บัญชีของฉัน' }}
                    </span>
                  </button>
                  <div class="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div class="bg-white dark:bg-[#111827] text-gray-900 dark:text-white rounded-2xl shadow-2xl py-2 min-w-[210px] border border-gray-100 dark:border-gray-800 overflow-hidden text-left">
                      <div class="px-4 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-white/5">
                        <p class="text-xs font-bold truncate">{{ authStore.currentUser?.first_name }} {{ authStore.currentUser?.last_name }}</p>
                        <p class="text-[10px] text-gray-500 dark:text-gray-400 truncate">{{ authStore.currentUser?.email }}</p>
                      </div>
                      <router-link to="/profile" class="px-4 py-2 text-xs font-medium hover:bg-orange-50 dark:hover:bg-orange-900/10 flex items-center gap-2">
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                        บัญชีของฉัน
                      </router-link>
                      <router-link to="/profile?tab=orders" class="px-4 py-2 text-xs font-medium hover:bg-orange-50 dark:hover:bg-orange-900/10 flex items-center gap-2">
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>
                        การสั่งซื้อของฉัน
                      </router-link>
                      <div class="border-t border-gray-100 dark:border-gray-800 my-1"></div>
                      <button @click="authStore.logout()" class="w-full text-left px-4 py-2 text-xs font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 flex items-center gap-2 cursor-pointer">
                        <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/></svg>
                        ออกจากระบบ
                      </button>
                    </div>
                  </div>
                </div>
                <button 
                  v-else-if="settingsStore.isOnlineShoppingEnabled"
                  @click="isLoginModalOpen = true" 
                  class="hidden md:flex flex-col items-center justify-center text-white/90 hover:text-orange-300 transition-colors px-1 py-1 cursor-pointer"
                  title="เข้าสู่ระบบ"
                >
                  <svg class="w-5 h-5 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span class="text-[10px] font-semibold mt-0.5 tracking-tight hidden sm:block">เข้าสู่ระบบ</span>
                </button>

                <!-- Dark Mode Toggle (Desktop) -->
                <button 
                  @click="toggleDarkMode" 
                  aria-label="สลับโหมดมืด" 
                  class="hidden md:flex p-2 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                >
                  <svg v-if="!isDark" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/></svg>
                  <svg v-else class="w-4 h-4 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
                </button>

                <!-- Mobile Hamburger Button -->
                <button 
                  @click="toggleMobileMenu" 
                  aria-label="เมนูหลัก" 
                  class="lg:hidden p-2 text-white hover:text-orange-300 focus:outline-none cursor-pointer"
                >
                  <svg v-if="!isMobileMenuOpen" class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
                  <svg v-else class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                </button>
              </div>
            </div>



            <!-- Menu Navigation Bar Row (เพิ่มเมนูครบตามภาพที่ผู้ใช้กำหนด) -->
            <div class="hidden lg:block border-t border-white/10 bg-black/25 backdrop-blur-xs">
              <div class="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between py-2 text-sm">
                
                <!-- Left: Complete Navigation Links -->
                <nav class="flex items-center gap-6 xl:gap-8 font-semibold">
                  
                  <!-- 1. โปรโมชั่น PROMO -->
                  <router-link 
                    to="/promotions" 
                    class="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 font-bold transition-colors py-1 group"
                  >
                    <svg class="w-4 h-4 text-amber-400 group-hover:scale-110 transition-transform shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
                    </svg>
                    <span>โปรโมชั่น</span>
                    <span class="inline-flex items-center px-1.5 py-0.2 rounded-full text-[10px] font-black tracking-wider bg-red-700 text-white shadow-xs">
                      PROMO
                    </span>
                  </router-link>

                  <!-- 3. บริการ -->
                  <router-link 
                    to="/services" 
                    :class="['transition-colors py-1', isActiveRoute('/services') ? 'text-orange-400 font-bold' : 'text-white/90 hover:text-white']"
                  >
                    บริการ
                  </router-link>

                  <!-- 4. คำนวณพื้นที่ -->
                  <router-link 
                    to="/space-calculator" 
                    :class="['transition-colors py-1', isActiveRoute('/space-calculator') ? 'text-orange-400 font-bold' : 'text-white/90 hover:text-white']"
                  >
                    คำนวณพื้นที่
                  </router-link>

                  <!-- 5. ผลงาน -->
                  <router-link 
                    to="/projects" 
                    :class="['transition-colors py-1', isActiveRoute('/projects') ? 'text-orange-400 font-bold' : 'text-white/90 hover:text-white']"
                  >
                    ผลงาน
                  </router-link>

                  <!-- 6. รู้จักเรา ˄ (Dropdown Card matching screenshot) -->
                  <div class="relative" @mouseenter="openAboutDropdown" @mouseleave="closeAboutDropdown">
                    <button 
                      type="button"
                      @click="isAboutDropdownOpen = !isAboutDropdownOpen"
                      :class="['inline-flex items-center gap-1 transition-colors py-1 cursor-pointer font-bold', (isActiveRoute('/about') || isActiveRoute('/blog') || isActiveRoute('/track-order') || isActiveRoute('/contact') || isAboutDropdownOpen) ? 'text-orange-400' : 'text-orange-400/90 hover:text-orange-300']"
                    >
                      <span>รู้จักเรา</span>
                      <svg class="w-3.5 h-3.5 transition-transform duration-200" :class="isAboutDropdownOpen ? 'rotate-180' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/>
                      </svg>
                    </button>

                    <!-- About Us Floating Dropdown Card (matching user screenshot) -->
                    <transition name="dropdown">
                      <div v-if="isAboutDropdownOpen" class="absolute top-full left-0 pt-2 z-50">
                        <div class="bg-white dark:bg-[#111827] rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.25)] border border-gray-100 dark:border-gray-800 p-3.5 min-w-[210px] space-y-1.5 text-left">
                          
                          <!-- เกี่ยวกับเรา -->
                          <router-link 
                            to="/about" 
                            @click="isAboutDropdownOpen = false"
                            class="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-orange-50/70 dark:hover:bg-orange-950/30 transition-all duration-200 group"
                          >
                            <div class="w-7 h-7 rounded-full border border-orange-500/50 text-orange-500 flex items-center justify-center shrink-0 group-hover:bg-orange-500 group-hover:text-white transition-all">
                              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                              </svg>
                            </div>
                            <span class="text-xs sm:text-sm font-bold text-gray-800 dark:text-gray-100 group-hover:text-orange-700 dark:group-hover:text-orange-300 transition-colors">
                              เกี่ยวกับเรา
                            </span>
                          </router-link>

                          <!-- บทความ -->
                          <router-link 
                            to="/blog" 
                            @click="isAboutDropdownOpen = false"
                            class="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-orange-50/70 dark:hover:bg-orange-950/30 transition-all duration-200 group"
                          >
                            <div class="w-7 h-7 rounded-full border border-orange-500/50 text-orange-500 flex items-center justify-center shrink-0 group-hover:bg-orange-500 group-hover:text-white transition-all">
                              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 002-2V7a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/>
                              </svg>
                            </div>
                            <span class="text-xs sm:text-sm font-bold text-gray-800 dark:text-gray-100 group-hover:text-orange-700 dark:group-hover:text-orange-300 transition-colors">
                              บทความ
                            </span>
                          </router-link>

                          <!-- ตรวจสอบคำสั่งซื้อ -->
                          <router-link 
                            to="/track-order" 
                            @click="isAboutDropdownOpen = false"
                            class="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-orange-50/70 dark:hover:bg-orange-950/30 transition-all duration-200 group"
                          >
                            <div class="w-7 h-7 rounded-full border border-orange-500/50 text-orange-500 flex items-center justify-center shrink-0 group-hover:bg-orange-500 group-hover:text-white transition-all">
                              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                              </svg>
                            </div>
                            <span class="text-xs sm:text-sm font-bold text-gray-800 dark:text-gray-100 group-hover:text-orange-700 dark:group-hover:text-orange-300 transition-colors">
                              ตรวจสอบคำสั่งซื้อ
                            </span>
                          </router-link>

                          <!-- ติดต่อเรา -->
                          <router-link 
                            to="/contact" 
                            @click="isAboutDropdownOpen = false"
                            class="flex items-center gap-3 px-3 py-2.5 rounded-2xl hover:bg-orange-50/70 dark:hover:bg-orange-950/30 transition-all duration-200 group"
                          >
                            <div class="w-7 h-7 rounded-full border border-orange-500/50 text-orange-500 flex items-center justify-center shrink-0 group-hover:bg-orange-500 group-hover:text-white transition-all">
                              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                              </svg>
                            </div>
                            <span class="text-xs sm:text-sm font-bold text-gray-800 dark:text-gray-100 group-hover:text-orange-700 dark:group-hover:text-orange-300 transition-colors">
                              ติดต่อเรา
                            </span>
                          </router-link>

                        </div>
                      </div>
                    </transition>
                  </div>

                </nav>

                <!-- Right: CTA Actions (ดูเส้นทาง + ขอใบเสนอราคา) -->
                <div class="flex items-center gap-3">
                  <!-- ดูเส้นทาง (Red Pill Button) -->
                  <a 
                    v-if="contactMapUrl"
                    :href="contactMapUrl" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    class="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#e11d48] hover:bg-[#be123c] active:bg-[#9f1239] text-white text-xs font-bold shadow-md shadow-rose-950/20 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                    title="ดูเส้นทาง (Google Maps)"
                  >
                    <svg class="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    <span>ดูเส้นทาง</span>
                  </a>
                  <router-link 
                    v-else 
                    to="/contact" 
                    class="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-[#e11d48] hover:bg-[#be123c] active:bg-[#9f1239] text-white text-xs font-bold shadow-md shadow-rose-950/20 transition-all hover:scale-105 whitespace-nowrap"
                  >
                    <svg class="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                    <span>ดูเส้นทาง</span>
                  </router-link>

                  <!-- ขอใบเสนอราคา (White Pill Button) -->
                  <router-link 
                    to="/quotation" 
                    class="inline-flex items-center px-4 py-1.5 rounded-full bg-white hover:bg-gray-100 active:bg-gray-200 text-gray-900 text-xs font-bold shadow-md transition-all hover:scale-105 whitespace-nowrap"
                  >
                    ขอใบเสนอราคา
                  </router-link>
                </div>

              </div>
            </div>

          </div>
        </div>

        <!-- Mobile Menu Drawer -->
        <transition name="slide-down">
          <div v-if="isMobileMenuOpen" class="lg:hidden absolute top-full left-0 w-full bg-white dark:bg-[#0a0f16] border-b border-gray-100 dark:border-white/10 shadow-2xl py-5 flex flex-col px-6 space-y-3 z-40 max-h-[85vh] overflow-y-auto">
            
            <!-- Mobile Search Bar (คลิกเพื่อเปิด GlobalSearch) -->
            <div 
              @click="isMobileMenuOpen = false; isSearchOpen = true" 
              class="relative mb-2 cursor-pointer group"
              role="button"
              tabindex="0"
              aria-label="ค้นหาสินค้า"
            >
              <div class="w-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-sm pl-4 pr-10 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 flex items-center justify-between group-hover:border-orange-500 transition-colors">
                <span>ค้นหาสินค้า บทความ...</span>
                <svg class="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                </svg>
              </div>
            </div>

            <!-- 1. สินค้า (Accordion) -->
            <div>
              <button @click="isMobileProductsOpen = !isMobileProductsOpen" :class="['py-2 text-base font-bold tracking-wide transition-colors flex items-center justify-between w-full', isActiveRoute('/products') ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-white hover:text-orange-600']">
                <span>สินค้า</span>
                <svg class="w-4 h-4 transition-transform duration-200" :class="isMobileProductsOpen ? 'rotate-180' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
              </button>
              <div v-if="isMobileProductsOpen" class="pl-4 pb-2 space-y-1 mt-1 border-l-2 border-orange-500/30">
                <router-link to="/products" @click="isMobileMenuOpen = false" class="block py-1.5 text-sm font-bold text-orange-600 dark:text-orange-400">ดูสินค้าทั้งหมด</router-link>
                <router-link v-for="cat in navCategories" :key="cat.id || cat.name" :to="`/products/category/${encodeURIComponent(cat.slug || cat.name)}`" @click="isMobileMenuOpen = false" class="block py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 font-medium">
                  {{ cat.name }}
                </router-link>
              </div>
            </div>

            <!-- 2. โปรโมชั่น PROMO -->
            <router-link to="/promotions" @click="isMobileMenuOpen = false" class="py-2 text-base font-bold tracking-wide transition-colors flex items-center justify-between text-amber-500">
              <span class="flex items-center gap-2">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"/>
                </svg>
                โปรโมชั่น
              </span>
              <span class="px-2 py-0.5 rounded-full text-[10px] font-black bg-red-700 text-white shadow-xs">PROMO</span>
            </router-link>

            <!-- 3. บริการ -->
            <router-link to="/services" @click="isMobileMenuOpen = false" :class="['py-2 text-base font-bold tracking-wide transition-colors', isActiveRoute('/services') ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-white hover:text-orange-600']">บริการ</router-link>

            <!-- 4. คำนวณพื้นที่ -->
            <router-link to="/space-calculator" @click="isMobileMenuOpen = false" :class="['py-2 text-base font-bold tracking-wide transition-colors', isActiveRoute('/space-calculator') ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-white hover:text-orange-600']">คำนวณพื้นที่</router-link>

            <!-- 5. ผลงาน -->
            <router-link to="/projects" @click="isMobileMenuOpen = false" :class="['py-2 text-base font-bold tracking-wide transition-colors', isActiveRoute('/projects') ? 'text-orange-600 dark:text-orange-400' : 'text-gray-900 dark:text-white hover:text-orange-600']">ผลงาน</router-link>

            <!-- 6. รู้จักเรา (Accordion) -->
            <div>
              <button @click="isMobileAboutOpen = !isMobileAboutOpen" class="py-2 text-base font-bold tracking-wide text-orange-600 dark:text-orange-400 flex items-center justify-between w-full">
                <span>รู้จักเรา</span>
                <svg class="w-4 h-4 transition-transform duration-200" :class="isMobileAboutOpen ? 'rotate-180' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>
              </button>
              <div v-if="isMobileAboutOpen" class="pl-4 pb-2 space-y-2 mt-1 border-l-2 border-orange-500/30">
                <router-link to="/about" @click="isMobileMenuOpen = false" class="flex items-center gap-2 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-orange-600">
                  <span class="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                  เกี่ยวกับเรา
                </router-link>
                <router-link to="/blog" @click="isMobileMenuOpen = false" class="flex items-center gap-2 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-orange-600">
                  <span class="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                  บทความ
                </router-link>
                <router-link to="/track-order" @click="isMobileMenuOpen = false" class="flex items-center gap-2 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-orange-600">
                  <span class="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                  ตรวจสอบคำสั่งซื้อ
                </router-link>
                <router-link to="/contact" @click="isMobileMenuOpen = false" class="flex items-center gap-2 py-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-orange-600">
                  <span class="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                  ติดต่อเรา
                </router-link>
              </div>
            </div>

            <!-- Mobile CTA Actions -->
            <div class="pt-4 mt-2 border-t border-gray-100 dark:border-white/10 flex flex-col gap-2.5">
              <a 
                v-if="contactMapUrl"
                :href="contactMapUrl" 
                target="_blank" 
                rel="noopener noreferrer" 
                class="flex justify-center items-center gap-2 py-3 rounded-xl bg-[#e11d48] text-white font-bold text-sm shadow-md"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
                <span>ดูเส้นทาง</span>
              </a>
              <router-link 
                to="/quotation" 
                @click="isMobileMenuOpen = false" 
                class="flex justify-center items-center py-3 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold text-sm shadow-md"
              >
                ขอใบเสนอราคา
              </router-link>

              <template v-if="settingsStore.isOnlineShoppingEnabled">
                <template v-if="authStore.isAuthenticated">
                  <div class="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 mt-1">
                    <img v-if="authStore.currentUser?.avatar_url && !mobileAvatarError" :src="authStore.currentUser?.avatar_url" @error="mobileAvatarError = true" class="w-10 h-10 rounded-full object-cover shadow-sm bg-white">
                    <div v-else class="w-10 h-10 rounded-full bg-orange-500 flex items-center justify-center text-white text-base font-black shadow-sm">
                      {{ authStore.currentUser?.first_name?.charAt(0)?.toUpperCase() || authStore.currentUser?.username?.charAt(0)?.toUpperCase() || 'U' }}
                    </div>
                    <div class="overflow-hidden">
                      <p class="text-sm font-bold text-gray-900 dark:text-white truncate">{{ authStore.currentUser?.first_name }} {{ authStore.currentUser?.last_name }}</p>
                      <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ authStore.currentUser?.email }}</p>
                    </div>
                  </div>
                  <router-link to="/profile" @click="isMobileMenuOpen = false" class="block w-full text-left py-1.5 text-sm font-bold text-gray-700 dark:text-gray-300 hover:text-orange-600 transition-colors">บัญชีของฉัน</router-link>
                  <router-link to="/profile?tab=orders" @click="isMobileMenuOpen = false" class="block w-full text-left py-1.5 text-sm font-bold text-gray-700 dark:text-gray-300 hover:text-orange-600 transition-colors">การสั่งซื้อของฉัน</router-link>
                  <router-link v-if="settingsStore.isWishlistEnabled" to="/profile?tab=wishlist" @click="isMobileMenuOpen = false" class="block w-full text-left py-1.5 text-sm font-bold text-gray-700 dark:text-gray-300 hover:text-orange-600 transition-colors">รายการโปรด</router-link>
                  <button @click="authStore.logout()" class="w-full text-left py-1.5 text-sm font-bold text-red-500 hover:text-red-600 transition-colors cursor-pointer">ออกจากระบบ</button>
                </template>
                <template v-else>
                  <button v-if="settingsStore.isWishlistEnabled" type="button" @click="handleWishlistNavClick" class="flex items-center justify-between w-full py-2 text-sm font-bold text-gray-700 dark:text-gray-300 hover:text-orange-600 transition-colors cursor-pointer">
                    <span class="flex items-center gap-2">
                      <svg class="w-4 h-4 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>
                      รายการโปรด
                    </span>
                    <span v-if="wishlistStore.items && wishlistStore.items.length > 0" class="px-2 py-0.5 rounded-full text-[10px] font-black bg-rose-500 text-white">{{ wishlistStore.items.length }}</span>
                  </button>
                  <button @click="isLoginModalOpen = true; isMobileMenuOpen = false" class="block w-full text-center py-3 rounded-xl border-2 border-orange-600 text-orange-600 dark:border-orange-500 dark:text-orange-400 font-bold tracking-wide transition-colors hover:bg-orange-50 dark:hover:bg-orange-900/10 text-sm cursor-pointer">
                    เข้าสู่ระบบ / สมัครสมาชิก
                  </button>
                </template>
              </template>

              <!-- Dark Mode Toggle inside Drawer -->
              <div class="flex items-center justify-between py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-white/10 mt-1 pt-2">
                <span class="flex items-center gap-2">
                  <svg v-if="!isDark" class="w-4 h-4 text-orange-600 dark:text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"/></svg>
                  <svg v-else class="w-4 h-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
                  โหมดการแสดงผล
                </span>
                <button @click="toggleDarkMode" class="px-3 py-1 rounded-full text-xs font-bold border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer">
                  {{ isDark ? 'โหมดมืด' : 'โหมดสว่าง' }}
                </button>
              </div>

              <!-- Mobile Drawer Quick Contact Info -->
              <div v-if="primaryContactPhone || primaryContactEmail" class="pt-3 border-t border-gray-100 dark:border-white/10 flex flex-col gap-2 text-xs text-gray-600 dark:text-gray-400">
                <a v-if="primaryContactPhone" :href="`tel:${primaryContactPhone}`" class="flex items-center gap-2 hover:text-orange-600 dark:hover:text-orange-400 transition-colors">
                  <svg class="w-4 h-4 text-orange-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                  <span class="font-medium">{{ primaryContactPhone }}</span>
                </a>
                <a v-if="primaryContactEmail" :href="`mailto:${primaryContactEmail}`" class="flex items-center gap-2 hover:text-orange-600 dark:hover:text-orange-400 transition-colors">
                  <svg class="w-4 h-4 text-orange-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                  <span class="font-medium truncate">{{ primaryContactEmail }}</span>
                </a>
              </div>
            </div>
          </div>
        </transition>
      </header>

      <!-- Mobile Menu Backdrop Overlay -->
      <transition
        enter-active-class="transition-opacity duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div 
          v-if="isMobileMenuOpen && !isAdminRoute" 
          data-testid="mobile-backdrop"
          class="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-xs z-40"
          @click="isMobileMenuOpen = false"
          aria-hidden="true"
        ></div>
      </transition>

      <main class="flex-grow min-h-screen relative z-10 flex flex-col">
      <router-view v-slot="{ Component, route }">
        <div :key="route.path" class="flex-grow flex flex-col w-full">
          <component :is="Component" />
        </div>
      </router-view>
    </main>

    <!-- Global Utilities -->
    <ToastContainer />
    <ConfirmModal />
    <div v-if="!isAdminRoute && route.path !== '/about'" style="overflow-anchor: none;">
      <AIProductRecommendation />
    </div>
    <CookieBanner v-if="!isAdminRoute" />
    <GlobalSearch v-model="isSearchOpen" />
    <LoginModal v-if="isLoginModalOpen" :isOpen="isLoginModalOpen" @close="isLoginModalOpen = false" />
    <BackToTop v-if="!isAdminRoute" />
    <CompareFloatingBar v-if="!isAdminRoute" />
    
    <!-- Floating Contact Hub -->
    <!-- Mobile Backdrop Blur Overlay -->
    <transition name="fade">
      <div 
        v-if="isContactHubOpen && !isAdminRoute" 
        class="fixed inset-0 bg-black/40 backdrop-blur-xs z-[55] md:hidden"
        @click="isContactHubOpen = false"
      ></div>
    </transition>

    <div 
      v-if="!isAdminRoute"
      ref="contactHubRef"
      :class="[
        hasMobileBottomBar ? 'hidden md:flex' : 'flex',
        'fixed bottom-20 sm:bottom-24 right-4 sm:right-6 z-[60] flex-col items-end'
      ]"
    >
      <!-- Modern Glassmorphism Contact Hub Panel -->
      <transition name="contact-hub">
        <div 
          v-if="isContactHubOpen" 
          class="contact-hub-card mb-2.5 w-[350px] sm:w-[370px] max-w-[calc(100vw-2rem)] max-h-[calc(100dvh-170px)] sm:max-h-[calc(100dvh-180px)] rounded-[24px] overflow-hidden bg-white/95 dark:bg-[#111622]/95 backdrop-blur-2xl shadow-2xl shadow-orange-950/25 dark:shadow-black/80 border border-slate-200/90 dark:border-white/10 text-slate-800 dark:text-slate-100 flex flex-col z-50 transform origin-bottom-right"
        >
          <!-- Header: Brand Identity & Active Availability Status (Always anchored at top) -->
          <div class="px-4 py-3 bg-gradient-to-r from-orange-500/10 via-amber-500/5 to-transparent dark:from-orange-500/15 dark:via-amber-500/5 dark:to-transparent border-b border-slate-100 dark:border-white/10 flex items-center justify-between shrink-0">
            <div class="flex items-center gap-2.5">
              <div class="w-9 h-9 rounded-xl bg-gradient-to-tr from-[#ea580c] to-[#f97316] text-white flex items-center justify-center font-black text-sm shadow-md shadow-orange-500/30 shrink-0">
                OL
              </div>
              <div>
                <h3 class="font-extrabold text-sm leading-tight text-slate-900 dark:text-white">ติดต่อ Outdoor Lander</h3>
                <div class="flex items-center gap-1.5 text-[11px] font-medium text-amber-600 dark:text-amber-400 mt-0.5">
                  <span class="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                  <span>พร้อมให้บริการ • 08:30 - 17:30 น.</span>
                </div>
              </div>
            </div>
            <button 
              @click.stop="isContactHubOpen = false"
              class="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/20 text-slate-500 dark:text-slate-300 flex items-center justify-center transition-colors cursor-pointer"
              aria-label="ปิดเมนูติดต่อ"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>

          <!-- Body: Structured Clean Channels (Flex-1 + Scrollable when screen is short) -->
          <div class="p-3.5 sm:p-4 space-y-3 flex-1 min-h-0 overflow-y-auto overscroll-contain scrollbar-thin">
            
            <!-- 1. Featured LINE Official Channel -->
            <a 
              v-for="(line, i) in contactLines" 
              :key="'line-'+i"
              :href="line.url || 'https://line.me/ti/p/~' + line.value"
              target="_blank" 
              rel="noopener noreferrer"
              class="group relative block rounded-2xl p-3 bg-gradient-to-r from-[#06C755] to-[#04a243] hover:from-[#05b84e] hover:to-[#03933c] text-white shadow-lg shadow-[#06C755]/25 hover:shadow-xl hover:shadow-[#06C755]/35 hover:-translate-y-0.5 transition-all duration-300"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2.5">
                  <div class="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                    <svg viewBox="0 0 24 24" class="w-4.5 h-4.5 fill-white"><path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63h2.386c.349 0 .63.285.63.63 0 .349-.281.63-.63.63H17.61V9.86h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.205 0 .391.09.51.253l2.444 3.317V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.627-.63.349 0 .631.285.631.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.281.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/></svg>
                  </div>
                  <div>
                    <div class="text-xs font-black tracking-wide">{{ line.name ? (line.name.startsWith('LINE') ? line.name : 'LINE — ' + line.name) : 'LINE Official Account' }}</div>
                    <div class="text-[11px] opacity-90 font-medium">{{ line.value }} • ตอบไว</div>
                  </div>
                </div>
                <span class="inline-flex items-center gap-1 bg-white text-[#06C755] text-xs font-black px-3 py-1.5 rounded-full shadow-sm group-hover:scale-105 transition-transform">
                  <span>แชทเลย</span>
                  <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/></svg>
                </span>
              </div>
            </a>

            <!-- 2. AI Consultant (If enabled) -->
            <router-link 
              v-if="settingsStore.isAiConsultantEnabled"
              to="/ai-consultant"
              @click="isContactHubOpen = false"
              class="group block rounded-2xl p-2.5 bg-gradient-to-r from-violet-600/10 via-indigo-600/5 to-transparent dark:from-violet-500/20 dark:via-indigo-500/10 dark:to-transparent border border-violet-200/70 dark:border-violet-500/20 hover:border-violet-400 dark:hover:border-violet-400/50 transition-all"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2.5">
                  <div class="w-7 h-7 rounded-lg bg-violet-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                  </div>
                  <div>
                    <div class="text-xs font-black text-slate-900 dark:text-white">AI ผู้ช่วยส่วนตัว 24 ชม.</div>
                    <div class="text-[10.5px] text-slate-500 dark:text-slate-400">แนะนำสเปกและคำนวณพื้นที่</div>
                  </div>
                </div>
                <span class="text-xs font-bold text-violet-600 dark:text-violet-400 group-hover:translate-x-0.5 transition-transform">คุยฟรี ➔</span>
              </div>
            </router-link>

            <!-- 3. Direct Phone Calls Hub -->
            <div class="space-y-1">
              <div class="flex items-center justify-between px-1">
                <span class="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                  <svg class="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.1 1.22 2 2 0 012.1 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14.92z"/></svg>
                  <span>เบอร์โทรศัพท์ติดต่อโดยตรง</span>
                </span>
              </div>
              <div class="bg-slate-50 dark:bg-white/[0.04] border border-slate-200/80 dark:border-white/5 rounded-2xl divide-y divide-slate-200/70 dark:divide-white/5 overflow-hidden">
                <a 
                  v-for="(phone, i) in contactPhones" 
                  :key="'phone-'+i"
                  :href="'tel:' + phone.value.replace(/[^0-9+]/g, '')"
                  class="flex items-center justify-between p-2 sm:p-2.5 hover:bg-orange-50/80 dark:hover:bg-orange-950/20 transition-colors group"
                >
                  <div class="flex items-center gap-2.5">
                    <div class="w-7 h-7 rounded-lg bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400 flex items-center justify-center shrink-0">
                      <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><path stroke-linecap="round" stroke-linejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                    </div>
                    <div>
                      <div class="text-xs font-bold text-slate-800 dark:text-slate-200">{{ phone.name || 'เบอร์ติดต่อ' }}</div>
                      <div class="text-[11px] font-mono font-medium text-slate-500 dark:text-slate-400">{{ phone.value }}</div>
                    </div>
                  </div>
                  <span class="inline-flex items-center gap-1 text-[11px] font-extrabold text-orange-600 dark:text-orange-400 bg-white dark:bg-white/5 group-hover:bg-orange-600 group-hover:text-white dark:group-hover:bg-orange-500 px-2.5 py-1 rounded-lg border border-orange-200/80 dark:border-orange-500/20 transition-all duration-200 shadow-2xs">
                    <span>โทร</span>
                    <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3"/></svg>
                  </span>
                </a>
              </div>
            </div>

            <!-- 4. Showroom & Google Maps Location -->
            <a 
              v-if="contactMapUrl"
              :href="contactMapUrl"
              target="_blank" 
              rel="noopener noreferrer"
              class="group block rounded-2xl p-2.5 bg-gradient-to-r from-red-500/10 via-rose-500/5 to-transparent dark:from-red-500/20 dark:via-rose-500/10 dark:to-transparent border border-red-200/80 dark:border-red-500/20 hover:border-red-400/80 dark:hover:border-red-400/60 hover:shadow-md transition-all"
            >
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2.5">
                  <div class="w-7 h-7 rounded-lg bg-[#EA4335] text-white flex items-center justify-center shrink-0 shadow-sm">
                    <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <div class="text-xs font-bold text-slate-800 dark:text-slate-200">เยี่ยมชมโชว์รูมตัวจริง</div>
                    <div class="text-[10px] text-slate-500 dark:text-slate-400">คลองหนึ่ง คลองหลวง ปทุมธานี</div>
                  </div>
                </div>
                <span class="inline-flex items-center gap-1 bg-[#EA4335] text-white text-[11px] font-black px-2.5 py-1 rounded-lg shadow-sm group-hover:scale-105 transition-transform">
                  <span>แผนที่</span>
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                </span>
              </div>
            </a>

            <!-- 5. Email Addresses -->
            <div v-if="contactEmails && contactEmails.length > 0" class="space-y-1 pt-0.5">
              <span class="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5 px-1">
                <svg class="w-3.5 h-3.5 text-slate-400 dark:text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                <span>อีเมลฝ่ายขาย & ฝ่ายบริการ</span>
              </span>
              <div class="grid grid-cols-1 gap-1">
                <a 
                  v-for="(email, i) in contactEmails" 
                  :key="'email-'+i"
                  :href="'mailto:' + email.value"
                  class="flex items-center justify-between p-2 rounded-xl bg-slate-50 hover:bg-slate-100 dark:bg-white/[0.03] dark:hover:bg-white/[0.07] border border-slate-100 dark:border-white/5 transition-colors group"
                >
                  <div class="truncate mr-2">
                    <div class="text-[11px] font-bold text-slate-800 dark:text-slate-200 truncate">{{ email.name || 'อีเมล' }}</div>
                    <div class="text-[10px] font-mono text-slate-500 dark:text-slate-400 truncate">{{ email.value }}</div>
                  </div>
                  <span class="text-[10px] font-bold text-slate-400 group-hover:text-orange-600 dark:group-hover:text-orange-400 shrink-0">ส่งเมล ➔</span>
                </a>
              </div>
            </div>

          </div>

          <!-- Footer Trust Note -->
          <div class="px-4 py-2 bg-slate-50 dark:bg-white/[0.02] border-t border-slate-100 dark:border-white/5 text-center shrink-0">
            <p class="text-[10px] text-slate-500 dark:text-slate-400 font-medium">ยินดีให้คำปรึกษาและประเมินราคาฟรี โดยไม่มีข้อผูกมัด</p>
          </div>
        </div>
      </transition>

      <!-- Main Floating Trigger Button (No green dot!) -->
      <button 
        @click.stop="toggleContactHub"
        :aria-expanded="isContactHubOpen"
        aria-label="ติดต่อเราด่วน"
        class="relative w-13 h-13 sm:w-14 sm:h-14 rounded-full bg-gradient-to-tr from-[#ea580c] via-[#f97316] to-[#fb923c] text-white shadow-xl shadow-orange-600/35 hover:shadow-orange-600/50 hover:scale-105 active:scale-95 transition-all duration-300 flex items-center justify-center cursor-pointer ring-4 ring-orange-500/20 z-50 group"
      >
        <!-- Chat Icon when closed -->
        <transition name="fade">
          <div v-if="!isContactHubOpen" class="flex items-center justify-center">
            <svg class="w-6 h-6 transform group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <!-- Close Icon when open -->
          <div v-else class="flex items-center justify-center">
            <svg class="w-6 h-6 transform rotate-0 hover:rotate-90 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </transition>
      </button>
    </div>

    <div v-if="!isAdminRoute" style="overflow-anchor: none;">
      <MainFooter />
    </div>
    <HistoryFloatingButton v-if="!isAdminRoute" />
    </template>
  </div>
</template>

<style>

/* Global Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Dropdown transition */
.dropdown-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.97);
}

body {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* Base custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-track {
  background: transparent; 
}
::-webkit-scrollbar-thumb {
  background: #cbd5e1; 
  border-radius: 4px;
}
.dark ::-webkit-scrollbar-thumb {
  background: #334155; 
}


/* Contact Hub Modern Card Animation */
.contact-hub-enter-active {
  transition: all 0.32s cubic-bezier(0.16, 1, 0.3, 1);
}
.contact-hub-leave-active {
  transition: all 0.22s cubic-bezier(0.4, 0, 1, 1);
}
.contact-hub-enter-from {
  opacity: 0;
  transform: scale(0.92) translateY(14px);
}
.contact-hub-leave-to {
  opacity: 0;
  transform: scale(0.94) translateY(10px);
}

</style>
